/* eslint-disable @typescript-eslint/no-use-before-define */
import { useEffect, useState } from 'react'

import useStateNonblocking from '@Hooks/useStateNonblocking'
import ArrowDown from '@Icons/arrow-down-table.svg?react'
import type { SortDirection } from '@Store/types/filter'
import type { Tracker } from '@Store/types/product'

import AddUrlForm from './product-table/add-url-form'
import TrackerRow from './product-table/tracker-row'

type ProductCompetitionTableProps = {
  product: any
  additionalCost?: number
  totalPrice?: any
  monitoringSource?: string
}

function ProductCompetitionTable({
  product,
  additionalCost,
  totalPrice,
  monitoringSource,
}: ProductCompetitionTableProps): JSX.Element {
  const [isEditing, setEditing] = useState<number | null>(null)
  const [sortColumn, setSortColumn] = useState<keyof Tracker>('calculatedPrice')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [trackers, setTrackers] = useState<Tracker[]>(product?.tracker ?? [])
  const [isAddingUrl, setIsAddingUrl] = useState<boolean>(false)
  const [warning, setWarning] = useState<string>()
  const [TrackerList, isTrackerListPending] = useStateNonblocking<
    JSX.Element[]
  >(
    () =>
      trackers?.map((tracker: any) => (
        <TrackerRow
          setWarning={setWarning}
          key={tracker.id}
          tracker={tracker}
          position={product.position}
          additionalCost={additionalCost}
          productId={product?.id}
          setEditing={setEditing}
          isEditing={isEditing}
          showTotalPrice={totalPrice}
        />
      )),
    [
      additionalCost,
      totalPrice,
      isEditing,
      product?.id,
      product.position,
      trackers,
    ],
    [],
  )

  const sortData = (column: keyof Tracker) => (): void => {
    let direction: SortDirection = 'asc'
    if (column === sortColumn) {
      direction = sortDirection === 'asc' ? 'desc' : 'asc'
    }
    const sortedData = trackers
      ? [...trackers].sort((a, b) => {
          if ((a[column] ?? 0) > (b[column] ?? 0)) {
            return direction === 'asc' ? 1 : -1
          } else if ((a[column] ?? 0) < (b[column] ?? 0)) {
            return direction === 'asc' ? -1 : 1
          } else {
            return 0
          }
        })
      : trackers
    setTrackers(sortedData)
    setSortColumn(column)
    setSortDirection(direction)
  }

  useEffect(() => {
    setTrackers(product.tracker)
  }, [product])

  return (
    <>
      <div className="relative overflow-x-auto md:overflow-x-visible border-t lg:border border-solid border-gray-200 lg:rounded-lg">
        <table className="w-full text-left">
          <thead className="border-b border-gray-200 font-medium text-sm text-gray-500 w-full">
            <tr className="space-x-2">
              <th
                scope="col"
                className="pl-6 py-3 pr-1 font-medium text-xs text-gray-500  md:w-[220px] lg:w-[250px]"
              >
                <button onClick={sortData('siteName')}>Company</button>
              </th>
              <th
                scope="col"
                className="py-3 px-1 font-medium text-xs text-gray-500 text-center"
              >
                <button onClick={sortData('calculatedPrice')}>
                  <div className="flex flex-row items-center">
                    <div className="pr-1">Price</div>
                    <ArrowDown
                      className={`${
                        sortColumn === 'calculatedPrice' &&
                        sortDirection === 'asc'
                          ? 'rotate-180'
                          : ''
                      }`}
                    />
                  </div>
                </button>
              </th>
              {totalPrice && (
                <th
                  scope="col"
                  className="lg:py-3 px-1 font-medium text-xs text-gray-500"
                >
                  <div className="flex flex-row items-center">
                    <div className="pr-1">Total Price</div>
                    <ArrowDown />
                  </div>
                </th>
              )}
              <th
                scope="col"
                className="py-3 px-1 font-medium text-xs text-gray-500 text-center"
              >
                <button onClick={sortData('urlChangeDay')}>Change</button>
              </th>
              <th
                scope="col"
                className="py-3 px-1 font-medium text-xs text-gray-500 text-center"
              >
                <button onClick={sortData('calculatedPrice')}>
                  <div className="flex flex-row items-center">
                    <div className="pr-1">Position</div>
                    <ArrowDown
                      className={`${
                        sortColumn === 'calculatedPrice' &&
                        sortDirection === 'asc'
                          ? 'rotate-180'
                          : ''
                      }`}
                    />
                  </div>
                </button>
              </th>
              <th
                scope="col"
                className="py-3 px-1 font-medium text-xs text-gray-500 text-center"
              >
                <button onClick={sortData('urlInStock')}>Stock</button>
              </th>
              <th
                scope="col"
                className="py-3 px-1 font-medium text-xs text-gray-500"
              >
                Last update
              </th>
              <th
                scope="col"
                className="py-3 px-1 font-medium text-xs text-gray-500"
              >
                {monitoringSource !== 'DATAEDIS' && 'Actions'}
              </th>
              {additionalCost && (
                <th
                  scope="col"
                  className="py-3 pl-1 font-medium text-xs text-gray-500"
                >
                  Additional Cost
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {isTrackerListPending ? (
              <tr>
                <td colSpan={8}>
                  <ProductCompetitionTableLoading />
                </td>
              </tr>
            ) : (
              TrackerList
            )}
          </tbody>
        </table>
        <div className="flex flex-col lg:flex-row justify-between lg:items-center px-6 pb-4 pt-3">
          {product.tracker ? (
            <p className="font-medium text-sm text-gray-700 pb-3 sm:pb-0">
              {product.tracker?.length} link(s) are found
              {product.tracker?.length !== 0 && (
                <span className="inline">
                  , your company is
                  <button className="mx-1">
                    <span>
                      #
                      {product.tracker?.findIndex(
                        (tracker: any) => tracker.is_mine,
                      ) + 1}
                    </span>
                  </button>
                  in competition
                </span>
              )}
            </p>
          ) : (
            <p className="font-medium text-sm text-gray-700"> Link not found</p>
          )}
        </div>
        {product?.id && (
          <AddUrlForm
            isAddingUrl={isAddingUrl}
            setIsAddingUrl={setIsAddingUrl}
            productId={product.id}
            warning={warning}
            setWarning={setWarning}
            hasIsMineUrl={trackers?.find((item) => item.is_mine) !== undefined}
          />
        )}
      </div>
    </>
  )
}

export default ProductCompetitionTable

function ProductCompetitionTableLoading(): JSX.Element {
  return (
    <div className="w-full border-gray-200">
      <div className="flex flex-row items-center justify-between h-12 w-full space-x-2 px-2">
        <div className="w-5/12 animate-pulse bg-gray-200 h-6 rounded"></div>
        <div className="w-1/12 animate-pulse bg-gray-200 h-6 rounded"></div>
        <div className="w-1/12 animate-pulse bg-gray-200 h-6 rounded"></div>
        <div className="w-1/12 animate-pulse bg-gray-200 h-6 rounded"></div>
        <div className="w-1/12 animate-pulse bg-gray-200 h-6 rounded"></div>
        <div className="w-1/12 animate-pulse bg-gray-200 h-6 rounded"></div>
        <div className="w-1/12 animate-pulse bg-gray-200 h-6 rounded"></div>
      </div>
    </div>
  )
}
